import type { BaseSyntheticEvent } from 'react';
import { useCallback, useContext, useState } from 'react';
import type { FormState, SubmitHandler, UseFormRegister } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useErrorBoundary } from 'react-error-boundary';
import { getCurrentTimestampMs } from '../../../utils/time';
import { validateAllowDomains } from '../utils/validateAllowDomains';
import { validateDisallowDomains } from '../utils/validateDisallowDomains';
import { putAccount } from '#/api/apiClient';
import { allowDomains, disallowDomains } from '#/utils/env';
import { SnackbarContext } from '#/utils/snackbar/snackbarProvider';

export const inputs = z.object({
  email: z
    .string()
    .min(1, { message: 'メールアドレスの形式が正しくありません' })
    .email('メールアドレスの形式が正しくありません')
    .refine(
      (email) => {
        const [, domain] = email.split('@');
        // emailバリデーションを通っているので、emailの中に必ず@は入っていてsplitにより配列の要素は２つになる想定
        // 許可リストに含まれていることを確認した上で、禁止リストに含まれていることを確認する流れとしているため && でチェックを行う
        return validateAllowDomains(allowDomains, domain!) && validateDisallowDomains(disallowDomains, domain!);
      },
      (email) => ({ message: `${email.split('@')[1]} が含まれるメールアドレスはご利用いただけません` })
    ),
});
export type InputsType = z.infer<typeof inputs>;

export type UseChangeFormProps = {
  isSuccess: boolean;
  email: string;
  register: UseFormRegister<InputsType>;
  onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  formState: FormState<InputsType>;
};

export function useChangeForm(): UseChangeFormProps {
  const { showBoundary } = useErrorBoundary();
  const snackbar = useContext(SnackbarContext);
  const { mutateAsync } = useMutation({
    mutationFn: (email: string) => putAccount({ email }),
  });
  const { register, handleSubmit, formState } = useForm<InputsType>({
    resolver: zodResolver(inputs),
    mode: 'onBlur',
  });
  const [isSuccess, setIsSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const onSubmit: SubmitHandler<InputsType> = useCallback(
    async (data) => {
      const result = await mutateAsync(data.email);
      if (result.response.ok) {
        setEmail(data.email);
        setIsSuccess(true);
        return;
      }
      if (result.response.status === 400) {
        snackbar.add({
          id: getCurrentTimestampMs(),
          message: '入力されたメールアドレスは使用できません。別のメールアドレスを入力してください。',
          notificationLevel: 'error',
          hideCloseButon: false,
        });
        return;
      }
      if (result.response.status === 409) {
        snackbar.add({
          id: getCurrentTimestampMs(),
          message: '保護者のメールアドレスとして登録されているものはサブメールアドレスに使用できません。',
          notificationLevel: 'error',
          hideCloseButon: false,
        });
        return;
      }
      showBoundary(result.error);
    },
    [mutateAsync, showBoundary, snackbar]
  );
  return {
    register,
    isSuccess,
    email,
    onSubmit: handleSubmit(onSubmit),
    formState,
  };
}
